import React, { useMemo } from 'react';
import * as Styled from './styled';
import {
  getDimensionMetaMap,
  type Query,
  type QueryBuilderGroupRule,
  type QueryBuilderRule,
} from '../FilterRulesBuilder';
import { operatorMap } from '@components/filter/components/utils';
import * as Types from '@src/components/filter/types';
import { Tag, TagProps } from './Tag';

interface Props {
  filterMeta: Types.FilterMeta;
  query: Query;
  onDelete?: (path: number[], query: Query) => void;
}

const isEmpty = (value: any) => [undefined, null, ''].includes(value);

const formatOperator = (op = '') =>
  (operatorMap[op] || '').replace(/^\w|\s\w/gi, (p) => (p || '').toUpperCase());

const formatRule = (
  rule: QueryBuilderRule | undefined,
  dimensionsMeta: Record<string, Types.DimensionFieldMeta>,
) => {
  if (!rule) return null;
  const meta = dimensionsMeta[rule.field];
  if (!meta) return null;

  const values = rule.value.map((it) => it.label ?? it.value).filter((v) => !isEmpty(v));
  return {
    operator: formatOperator(rule.operator),
    values,
    dimension: meta.value === 'TAG' ? rule.key : meta.name,
  };
};

export const FilterRuleTags = (props: Props) => {
  const { query, filterMeta, onDelete } = props;

  const dimensionsMeta = useMemo(() => getDimensionMetaMap(filterMeta), [filterMeta]);

  const queryRules = query.rules
    .map((ruleGroup, groupIndex) => {
      const ruleGroups = (ruleGroup as QueryBuilderGroupRule).rules
        ? (ruleGroup as QueryBuilderGroupRule).rules
        : [ruleGroup];

      const groups = ruleGroups
        .map((it) => {
          const ruleInfo = formatRule(it as QueryBuilderRule, dimensionsMeta);
          if (!ruleInfo || isEmpty(ruleInfo.dimension) || !ruleInfo.values.length) return null;
          return ruleInfo;
        })
        .filter(Boolean);

      if (!groups.length) return null;
      return {
        path: [groupIndex],
        groups,
      };
    })
    .filter(Boolean) as any as TagProps['data'][];

  const renderContent = () => {
    return queryRules.map((it, index) => {
      const handleOnDelete = () => onDelete?.((it as any).path, query);
      return <Tag key={index} data={it} onDelete={handleOnDelete} />;
    });
  };

  return (
    <Styled.Container>
      <Styled.FilterIcon />
      {renderContent()}
    </Styled.Container>
  );
};
